import React, { PropsWithChildren, ReactNode } from 'react';

import { useHistory } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';

type Props = PropsWithChildren<{
  title: ReactNode;
  subtitle?: ReactNode;
  backButton?: string | boolean;
  sticky?: boolean;
  bottomSpacing?: number;
}>;

/**
 * PageHeader - A generic page header component.
 *
 * For user-specific features (preferred agent, fraud warning), render those
 * directly in the page component rather than passing user data here.
 */
export default function PageHeader({
  title,
  children,
  subtitle,
  backButton,
  sticky = false,
  bottomSpacing = 3,
}: Props) {
  const history = useHistory();

  const backButtonProps = () => {
    if (typeof backButton === 'string') {
      return { to: backButton, component: Link };
    }

    return { onClick: goBack };
  };

  const goBack = () => {
    history.goBack();
  };

  return (
    <Box
      pt={1}
      pb={bottomSpacing}
      sx={sticky ? { position: 'sticky', top: 0, zIndex: 1000, backgroundColor: 'white' } : undefined}
    >
      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
        {backButton && (
          <IconButton sx={{ border: 1 }} size="small" color="secondary" title="Back" {...backButtonProps()}>
            <ArrowBackIcon />
          </IconButton>
        )}

        {!subtitle && (
          <Typography flex={1} variant="h1">
            {title}
          </Typography>
        )}

        {!!subtitle && (
          <Stack direction="column" gap={0.5}>
            <Typography flex={1} variant="h1">
              {title}
            </Typography>
            <Typography variant="h2">{subtitle}</Typography>
          </Stack>
        )}

        {children && <Box>{children}</Box>}
      </Stack>

      <Divider />
    </Box>
  );
}
